import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gaTrackKPSDownload, gaTrackKPSPageView, gaTrackKPSSectionView } from '../../services/gaAnalyticsService';

const features = [
  {
    icon: '🛡️',
    title: 'Scellé Anti-Fraude HMAC SHA-256',
    description: 'Les archives .kps contiennent une empreinte cryptographique. Toute tentative de modification du prix ou des données du bien invalide automatiquement le fichier.',
    span: 'col-span-1 md:col-span-2',
    accent: '#2563eb'
  },
  {
    icon: '👁️',
    title: 'Visites Virtuelles 360° Intégrées',
    description: 'Projetez et organisez vos panoramas 360° et modèles 3D (.glb) pour une immersion totale de vos acheteurs et notaires.',
    span: 'col-span-1',
    accent: '#0891b2'
  },
  {
    icon: '💳',
    title: 'Paiements Mobile Money',
    description: 'Gestion directe des réservations et des acomptes en séquestre via Mobile Money (XOF) et cartes bancaires avec confirmation instantanée.',
    span: 'col-span-1',
    accent: '#7c3aed'
  },
  {
    icon: '⚖️',
    title: 'Tableau de Bord Notaires',
    description: 'Connexion en temps réel avec le panneau "Accès Notaires" pour valider les identités et approuver le déblocage des fonds sous double authentification 2FA.',
    span: 'col-span-1',
    accent: '#059669'
  },
  {
    icon: '⚡',
    title: 'Ouverture Directe au Double-Clic',
    description: 'Association automatique de l\'extension .kps dans Windows. Double-cliquez sur n\'importe quel fichier de propriété pour le charger instantanément dans le studio.',
    span: 'col-span-1',
    accent: '#d97706'
  },
  {
    icon: '🔄',
    title: 'Mises à Jour Automatiques',
    description: 'Recevez les correctifs de sécurité et nouvelles fonctionnalités en arrière-plan sans réinstaller le logiciel.',
    span: 'col-span-1 md:col-span-2',
    accent: '#2563eb'
  }
];

const steps = [
  {
    number: '01',
    title: 'Téléchargez le fichier',
    description: <>Cliquez sur le bouton de téléchargement pour obtenir <code className="font-mono text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400">KPS_Creator_Setup_1.0.0.exe</code>.</>
  },
  {
    number: '02',
    title: 'Lancez l\'installateur',
    description: 'Exécutez l\'assistant NSIS et choisissez le dossier d\'installation sur votre ordinateur Windows.'
  },
  {
    number: '03',
    title: 'Ouvrez vos fichiers .kps',
    description: <>Lancez le studio ou double-cliquez directement sur une archive <code className="font-mono text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400">.kps</code> pour démarrer.</>
  }
];

const specs = [
  { component: 'Système d\'exploitation', min: 'Windows 10 (64-bit)', recommended: 'Windows 11 (64-bit)' },
  { component: 'Processeur', min: 'Intel Core i3 / AMD Ryzen 3', recommended: 'Intel Core i5 / AMD Ryzen 5 ou supérieur' },
  { component: 'Mémoire RAM', min: '4 Go RAM', recommended: '8 Go RAM ou plus' },
  { component: 'Espace disque', min: '200 Mo pour le logiciel', recommended: '500 Mo SSD' },
  { component: 'Connexion Internet', min: 'Requise pour la validation', recommended: 'Haut débit stable (Fibre / 4G)' }
];

const KPSCreatorStudio = () => {
  const featuresRef = useRef(null);
  const specsRef = useRef(null);
  const installRef = useRef(null);

  useEffect(() => {
    gaTrackKPSPageView({ referrer: document?.referrer });
  }, []);

  useEffect(() => {
    const sections = [
      { ref: featuresRef, name: 'features' },
      { ref: specsRef, name: 'specs' },
      { ref: installRef, name: 'installation' },
    ];

    const observers = sections?.map(({ ref, name }) => {
      if (!ref?.current) return null;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              gaTrackKPSSectionView(name);
              observer.disconnect();
            }
          });
        },
        { threshold: 0.3 }
      );
      observer?.observe(ref?.current);
      return observer;
    });

    return () => {
      observers?.forEach((obs) => obs?.disconnect());
    };
  }, []);

  const handleDownloadClick = (source = 'hero_cta') => {
    gaTrackKPSDownload({ version: '1.0.0', platform: 'Windows', source });
  };

  return (
    <div
      className="min-h-screen text-slate-100 overflow-x-hidden outline-none border-none"
      style={{
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        backgroundColor: '#050505',
      }}
    >
      {/* Halo global */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-40" 
        style={{
          background: 'radial-gradient(circle at 50% -20%, rgba(37, 99, 235, 0.15) 0%, transparent 60%)'
        }} 
      />

      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 no-underline group">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black text-white shadow-lg shadow-blue-500/20"
              style={{ background: 'linear-gradient(135deg, #1d4ed8, #0891b2)' }}
            >
              KPS
            </div>
            <span className="font-heading font-bold text-base text-slate-200 group-hover:text-white transition-colors">
              Kapital States
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-2">
            {[
              { label: 'Accueil', to: '/', isLink: true },
              { label: 'Fonctionnalités', href: '#features' },
              { label: 'Configuration', href: '#specs' },
              { label: 'Support', href: 'mailto:contact@kapitalstates.com' }
            ]?.map((item, i) =>
              item?.isLink ? (
                <Link key={i} to={item?.to} className="px-4 py-2 text-sm font-medium text-white/50 hover:text-white rounded-full hover:bg-white/5 transition-all no-underline">
                  {item?.label}
                </Link>
              ) : (
                <a key={i} href={item?.href} className="px-4 py-2 text-sm font-medium text-white/50 hover:text-white rounded-full hover:bg-white/5 transition-all no-underline">
                  {item?.label}
                </a>
              )
            )}
            <div className="w-px h-5 mx-3 bg-white/10" />
            <Link
              to="/auth"
              className="px-5 py-2 text-sm font-bold text-black bg-white hover:bg-gray-200 rounded-full transition-all no-underline"
            >
              Accès Web →
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 pb-24 px-6 relative z-10">
        <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
          
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-10 bg-blue-500/10 text-blue-400 shadow-lg shadow-blue-500/10">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            v1.0.0 — Enterprise Release
          </div>

          <h1 className="text-6xl md:text-8xl font-heading font-extrabold leading-tight tracking-tighter mb-8 max-w-4xl">
            <span className="text-white">Logiciel de Gestion</span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-cyan-400 to-indigo-400">
              Paiements & Séquestre
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/50 max-w-2xl font-light leading-relaxed mb-12">
            Bloquez les fonds et automatisez leur déblocage après livraison de votre commande. Le premier outil de bureau certifié anti-fraude, conçu pour les notaires de Côte d'Ivoire.
          </p>

          {/* CTA Row */}
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* 
              ATTENTION : Le lien a été mis à jour avec le format correct (sans espaces) 
            */}
            <a
              href="https://rwtsvvpklmosipnatufe.supabase.co/storage/v1/object/public/Downloads/KPS_Creator_Setup_1.0.0.exe"
              download
              onClick={() => handleDownloadClick('hero_cta')}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-base font-bold text-white no-underline transition-transform hover:scale-105 active:scale-95 shadow-xl shadow-blue-600/30"
              style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Télécharger pour Windows
            </a>

            <div className="flex items-center gap-5 text-sm text-white/40 font-medium">
              <span className="flex items-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                Win 10/11
              </span>
              <span className="flex items-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                ~85 Mo
              </span>
            </div>
          </div>

          {/* Stats row - Style Apple */}
          <div className="mt-20 flex flex-wrap justify-center gap-8 sm:gap-16 w-full max-w-4xl">
            {[
              { value: 'HMAC SHA-256', label: 'Chiffrement Bancaire' },
              { value: '2FA TOTP', label: 'Authentification Notaires' },
              { value: '.kps', label: 'Format propriétaire certifié' }
            ]?.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold text-white mb-2 tracking-tight">{stat?.value}</div>
                <div className="text-xs font-bold uppercase tracking-widest text-white/30">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES — Asymmetric Bento (Zéro Bordure) */}
      <section id="features" className="py-24 px-6 relative z-10" ref={featuresRef}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-white mb-4">
              Conçu pour l'Écosystème Notarial
            </h2>
            <p className="text-white/50 text-lg font-light max-w-2xl mx-auto">
              Une technologie de pointe pour sécuriser vos transactions immobilières en Côte d'Ivoire.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {features?.map((f, i) => (
              <div
                key={i}
                className={`${f?.span} bg-[#111111] rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1 group shadow-2xl border-none`}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-6 shadow-lg"
                  style={{ background: `${f?.accent}20`, color: f?.accent }}
                >
                  {f?.icon}
                </div>
                <h3 className="text-lg font-heading font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">{f?.title}</h3>
                <p className="text-sm font-light text-white/50 leading-relaxed">{f?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTALLATION */}
      <section className="py-24 px-6" ref={installRef}>
        <div className="max-w-6xl mx-auto">
          <div className="bg-[#111111] rounded-3xl p-12 md:p-16 shadow-2xl border-none">
            <div className="mb-12 text-center">
              <h2 className="text-4xl font-heading font-bold tracking-tight text-white">
                Installation Rapide
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {steps?.map((step, i) => (
                <div key={i} className="relative text-center flex flex-col items-center">
                  {i < steps?.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-gradient-to-r from-blue-500/30 to-transparent" />
                  )}
                  <div className="w-16 h-16 rounded-full bg-blue-500/10 text-blue-400 flex items-center justify-center text-xl font-black mb-6 shadow-lg shadow-blue-500/10">
                    {step?.number}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-3">{step?.title}</h4>
                  <p className="text-sm font-light text-white/50 leading-relaxed">{step?.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SYSTEM SPECS */}
      <section id="specs" className="py-24 px-6" ref={specsRef}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-heading font-bold tracking-tight text-white">
              Prérequis Système
            </h2>
          </div>

          <div className="bg-[#111111] rounded-3xl overflow-hidden shadow-2xl border-none">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white/5 border-b border-white/5">
                  <th className="text-left px-8 py-5 text-xs font-bold text-white/40 uppercase tracking-widest">Composant</th>
                  <th className="text-left px-8 py-5 text-xs font-bold text-white/40 uppercase tracking-widest">Minimale</th>
                  <th className="text-left px-8 py-5 text-xs font-bold text-white/40 uppercase tracking-widest">Recommandée</th>
                </tr>
              </thead>
              <tbody>
                {specs?.map((row, i) => (
                  <tr key={i} className="border-b border-white/5 last:border-none hover:bg-white/[0.02] transition-colors">
                    <td className="px-8 py-6 text-sm font-bold text-white">{row?.component}</td>
                    <td className="px-8 py-6 text-sm font-light text-white/50">{row?.min}</td>
                    <td className="px-8 py-6 text-sm">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-400">
                        {row?.recommended}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-900 to-[#111111] rounded-3xl p-12 md:p-20 text-center relative overflow-hidden shadow-2xl border-none">
            {/* Effet lumineux interne */}
            <div className="absolute inset-0 bg-blue-500/10 blur-3xl rounded-full" />
            
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
                Prêt à sécuriser vos transactions ?
              </h2>
              <p className="text-lg font-light text-white/70 mb-10 max-w-xl mx-auto">
                Téléchargez KPS Creator Studio et commencez à certifier vos biens immobiliers dès aujourd'hui.
              </p>
              
              <a
                href="https://rwtsvvpklmosipnatufe.supabase.co/storage/v1/object/public/Downloads/KPS_Creator_Setup_1.0.0.exe"
                download
                onClick={() => handleDownloadClick('cta_bottom')}
                className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full text-lg font-bold text-white no-underline transition-transform hover:scale-105 active:scale-95 shadow-2xl shadow-blue-600/40"
                style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Télécharger KPS Creator Studio
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 bg-[#030303] border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs font-light text-white/40">
            © {new Date().getFullYear()} <span className="font-bold text-white/60">Kapital States</span> — Abidjan, Côte d'Ivoire
          </p>
          <div className="flex items-center gap-6 text-xs font-light text-white/40">
            <a href="mailto:contact@kapitalstates.com" className="hover:text-white transition-colors no-underline">
              contact@kapitalstates.com
            </a>
            <span>+225 07 49 47 52 80</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KPSCreatorStudio;